(function () {
  function normalizeVariantId(value) {
    const text = String(value || "").trim();
    const match = text.match(/ProductVariant\/(\d+)$/);
    return match ? match[1] : text;
  }

  function normalizeMediaId(value) {
    const text = String(value || "").trim();
    const match = text.match(/(?:MediaImage|Media|ProductImage)\/(\d+)$/);
    return match ? match[1] : text;
  }

  function imageUrlKey(value) {
    if (!value) return "";
    try {
      const url = new URL(String(value), window.location.origin);
      return decodeURIComponent(url.pathname)
        .split("/")
        .pop()
        .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|x\d+)(?=\.[^.]+$)/i, "")
        .toLowerCase();
    } catch (_) {
      return String(value).split("?")[0].split("/").pop().toLowerCase();
    }
  }

  function decimalPriceToCents(value) {
    const parsed = Number.parseFloat(String(value || ""));
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  }

  function formatMoney(cents) {
    const format = window.theme?.currencyCodeEnabled
      ? window.theme.moneyWithCurrencyFormat
      : window.theme?.moneyFormat;
    if (window.Currency?.formatMoney && format) {
      return window.Currency.formatMoney(cents, format);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  function urlHandle(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function optionPayloadKey(value) {
    return urlHandle(value).replace(/-/g, "_");
  }

  function encodeOptionsPayload(value) {
    const bytes = new TextEncoder().encode(JSON.stringify(value));
    let binary = "";
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return window
      .btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/g, "");
  }

  function decodeOptionsPayload(value) {
    if (!value) return {};
    try {
      const normalized = String(value).replace(/-/g, "+").replace(/_/g, "/");
      const padded = normalized.padEnd(
        normalized.length + ((4 - (normalized.length % 4)) % 4),
        "=",
      );
      const binary = window.atob(padded);
      const bytes = Uint8Array.from(binary, (character) =>
        character.charCodeAt(0),
      );
      const decoded = JSON.parse(new TextDecoder().decode(bytes));
      return decoded && typeof decoded === "object" ? decoded : {};
    } catch (_) {
      return {};
    }
  }

  function normalizeConfig(raw) {
    const optionId = "variant5";
    const config = Array.isArray(raw?.options)
      ? raw
      : raw?.values
        ? {
      options: [
        {
          id: optionId,
          name: raw.name || "Option",
          presentation: /^(color|colour|couleur)$/i.test(raw.name || "")
            ? "swatches"
            : "buttons",
          values: raw.values || [],
          translations: raw.translations || {},
        },
      ],
      variants: raw.variants || [],
      optionOrder: raw.optionOrder || [],
          }
        : { options: [], variants: [], optionOrder: [] };
    return {
      ...config,
      variants: (config.variants || []).map((mapping) => {
        const legacyImage = mapping.image
          ? [{
              url: mapping.image,
              mediaId: mapping.imageMediaId || "",
              alt: mapping.imageAlt || "",
            }]
          : [];
        return {
          ...mapping,
          optionValueIds:
            mapping.optionValueIds || { [optionId]: mapping.optionValueId },
          images: Array.isArray(mapping.images) && mapping.images.length
            ? mapping.images
            : legacyImage,
        };
      }),
    };
  }

  function sameSelection(left, right) {
    const leftKeys = Object.keys(left || {}).sort();
    const rightKeys = Object.keys(right || {}).sort();
    return (
      leftKeys.length === rightKeys.length &&
      leftKeys.every(
        (key, index) =>
          key === rightKeys[index] && String(left[key]) === String(right[key]),
      )
    );
  }

  class AppManagedVariantOptions extends HTMLElement {
    constructor() {
      super();
      this.currentVariant = null;
      this.mediaSelectionActive = false;
      this.boundVariantChanged = this.handleVariantChanged.bind(this);
      this.boundSelectionChanged = this.handleSelectionChanged.bind(this);
    }

    connectedCallback() {
      if (this.initialized) {
        this.bindEvents();
        this.observeAvailabilityState();
        this.applySelection();
        return;
      }
      this.initialized = true;
      const configElement = this.querySelector(
        "[data-app-managed-variant-option-config]",
      );
      try {
        this.config = normalizeConfig(
          JSON.parse(configElement?.textContent || "{}"),
        );
      } catch (error) {
        console.error("AppManagedVariantOptions: invalid configuration", error);
        return;
      }

      this.productScope =
        this.closest('[data-section-type="product"]') ||
        this.closest("[data-section-id]") ||
        document;
      this.productForm = this.closest("form");
      this.optionGroups = Array.from(
        this.querySelectorAll("[data-app-managed-option]"),
      );
      this.restoreSelectionsFromUrl();
      this.moveToSavedPosition();
      this.bindEvents();
      this.observeAvailabilityState();
      this.observeGalleryState();
      this.currentVariant = this.findCurrentVariant();
      this.applySelection();
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.currentVariant = this.findCurrentVariant();
          this.applySelection();
        }),
      );
      const applyAfterPageLoad = () => {
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(() => {
            if (!this.isConnected) return;
            this.currentVariant = this.findCurrentVariant();
            this.applySelection();
          }),
        );
      };
      if (document.readyState === "complete") {
        applyAfterPageLoad();
      } else {
        window.addEventListener("load", applyAfterPageLoad, { once: true });
      }
    }

    disconnectedCallback() {
      this.eventRoot?.removeEventListener(
        "change",
        this.boundSelectionChanged,
      );
      document.removeEventListener("variant:changed", this.boundVariantChanged);
      this.availabilityObserver?.disconnect();
      this.affirmObserver?.disconnect();
      this.galleryObserver?.disconnect();
    }

    bindEvents() {
      this.eventRoot?.removeEventListener(
        "change",
        this.boundSelectionChanged,
      );
      document.removeEventListener("variant:changed", this.boundVariantChanged);
      this.eventRoot = this.productForm || this.productScope;
      this.eventRoot.addEventListener("change", this.boundSelectionChanged);
      document.addEventListener("variant:changed", this.boundVariantChanged);
    }

    observeAvailabilityState() {
      this.availabilityObserver?.disconnect();
      this.availabilityObserver = new MutationObserver((mutations) => {
        mutations.forEach(({ target }) => {
          if (
            target instanceof HTMLInputElement &&
            target.dataset.appManagedUnavailable === "true" &&
            !target.disabled
          ) {
            target.disabled = true;
          }
        });
      });
      this.availabilityObserver.observe(this.productScope, {
        attributes: true,
        attributeFilter: ["disabled"],
        subtree: true,
      });
    }

    observeGalleryState() {
      this.galleryObserver?.disconnect();
      const gallery = this.productScope.querySelector(".product-gallery");
      if (!gallery) return;
      this.galleryObserver = new MutationObserver(() => {
        const mediaIds = this.activeMappedMediaIds || [];
        if (mediaIds.length === 0 || this.gallerySyncScheduled) return;
        const mediaIdSet = new Set(mediaIds);
        const cells = Array.from(
          gallery.querySelectorAll(".product-gallery__carousel-item[data-media-id]"),
        );
        const outOfSync = cells.some((cell) => {
          const isMapped = mediaIdSet.has(String(cell.dataset.mediaId || ""));
          return isMapped === cell.classList.contains("is-filtered");
        });
        if (!outOfSync) return;
        this.gallerySyncScheduled = true;
        window.requestAnimationFrame(() => {
          this.gallerySyncScheduled = false;
          if (!this.isConnected) return;
          this.productScope.dispatchEvent(
            new CustomEvent("app-managed-gallery:changed", {
              bubbles: true,
              detail: { mediaIds },
            }),
          );
        });
      });
      this.galleryObserver.observe(gallery, {
        attributes: true,
        attributeFilter: ["class"],
        subtree: true,
      });
    }

    moveToSavedPosition() {
      const parent = this.parentElement;
      if (!parent) return;
      [...this.optionGroups]
        .sort(
          (left, right) =>
            Number.parseInt(left.dataset.orderIndex || "", 10) -
            Number.parseInt(right.dataset.orderIndex || "", 10),
        )
        .forEach((group) => {
          const orderIndex = Number.parseInt(
            group.dataset.orderIndex || "",
            10,
          );
          const options = Array.from(parent.children).filter(
            (element) =>
              element !== group &&
              element.classList?.contains("product-form__option") &&
              !element.classList.contains("no-js"),
          );
          const target =
            options[
              Number.isFinite(orderIndex) ? orderIndex : options.length
            ];
          parent.insertBefore(group, target || this);
        });
    }

    findOptionGroup(optionId) {
      return this.optionGroups.find(
        (group) => String(group.dataset.optionId) === String(optionId),
      );
    }

    readProductData() {
      if (this.productData) return this.productData;
      const dataElement = this.productScope.querySelector("[data-product-json]");
      try {
        this.productData = JSON.parse(dataElement?.textContent || "{}");
      } catch (_) {
        this.productData = {};
      }
      return this.productData;
    }

    findCurrentVariant() {
      const selectedId = this.productForm?.querySelector('[name="id"]')?.value;
      const variants = this.readProductData()?.product?.variants || [];
      return (
        variants.find(
          (variant) =>
            normalizeVariantId(variant.id) === normalizeVariantId(selectedId),
        ) || null
      );
    }

    handleVariantChanged(event) {
      const eventScope =
        event.target?.closest?.('[data-section-type="product"]') ||
        event.target?.closest?.("[data-section-id]") ||
        document;
      if (
        eventScope !== document &&
        this.productScope !== document &&
        eventScope !== this.productScope
      ) {
        return;
      }
      this.currentVariant = event.detail?.variant || this.findCurrentVariant();
      this.applySelection();
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => {
          if (!this.isConnected) return;
          const selections = this.getSelections();
          if (Object.keys(selections).length === this.config.options.length) {
            this.updateOptionAvailability(selections);
          }
        }),
      );
      if (this.urlSelectionActive) {
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(() => {
            if (this.isConnected) this.updateUrl(this.getSelections());
          }),
        );
      }
    }

    handleSelectionChanged(event) {
      const isManagedOption =
        event.target.matches(".app-managed-variant-option__radio") &&
        this.optionGroups.some((group) => group.contains(event.target));
      const isNativeOption = event.target.matches(
        '.product-form__option[data-option-position]:not(.no-js) input[type="radio"]',
      );
      if (!isManagedOption && !isNativeOption) {
        return;
      }
      this.mediaSelectionActive = true;
      if (isManagedOption) {
        this.urlSelectionActive = true;
        this.applySelection();
        return;
      }
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.currentVariant = this.findCurrentVariant();
          this.applySelection();
        }),
      );
    }

    restoreSelectionsFromUrl() {
      const url = new URL(window.location.href);
      const encodedOptions = decodeOptionsPayload(
        url.searchParams.get("options"),
      );
      this.config.options.forEach((option) => {
        const requestedValue =
          encodedOptions[optionPayloadKey(option.name)] ??
          url.searchParams.get(urlHandle(option.name));
        if (!requestedValue) return;
        const requestedText = String(requestedValue);
        const requestedHandle = urlHandle(requestedText);
        const group = this.findOptionGroup(option.id);
        const radio = Array.from(
          group?.querySelectorAll(".app-managed-variant-option__radio") || [],
        ).find((input) => {
          const configuredValue = option.values.find(
            (value) => String(value.id) === String(input.value),
          );
          return (
            String(input.value) === requestedText ||
            urlHandle(input.dataset.label) === requestedHandle ||
            urlHandle(configuredValue?.label) === requestedHandle
          );
        });
        if (radio) {
          radio.checked = true;
          this.urlSelectionActive = true;
        }
      });
    }

    getSelections() {
      return this.config.options.reduce((selections, option) => {
        const group = this.findOptionGroup(option.id);
        const radio = group?.querySelector(
          ".app-managed-variant-option__radio:checked",
        );
        if (radio) {
          selections[option.id] = {
            id: radio.value,
            label: radio.dataset.label || radio.value,
            name: group.dataset.optionName || option.name,
          };
        }
        return selections;
      }, {});
    }

    findMapping(baseVariantId, selections) {
      const selectedIds = Object.fromEntries(
        Object.entries(selections).map(([optionId, selection]) => [
          optionId,
          selection.id,
        ]),
      );
      return (this.config.variants || []).find(
        (mapping) =>
          normalizeVariantId(mapping.baseVariantId) ===
            normalizeVariantId(baseVariantId) &&
          sameSelection(mapping.optionValueIds, selectedIds),
      );
    }

    applySelection() {
      let selections = this.getSelections();
      if (Object.keys(selections).length !== this.config.options.length) return;
      if (!this.currentVariant) this.currentVariant = this.findCurrentVariant();
      let mapping = this.findMapping(this.currentVariant?.id, selections);
      if (!mapping) {
        const recoveredSelections = this.recoverInvalidSelection(selections);
        if (recoveredSelections) {
          selections = recoveredSelections;
          mapping = this.findMapping(this.currentVariant?.id, selections);
        }
      }

      this.updateOptionAvailability(selections);
      this.updatePurchaseAvailability(Boolean(mapping));

      Object.entries(selections).forEach(([optionId, selection]) => {
        const group = this.findOptionGroup(optionId);
        const propertyInput = group?.querySelector(
          ".app-managed-variant-option__property",
        );
        const selectedLabel = group?.querySelector(
          "[data-managed-selected-label]",
        );
        if (propertyInput) propertyInput.value = selection.label;
        if (selectedLabel) selectedLabel.textContent = selection.label;
      });

      this.updateSku(mapping);
      this.updatePrices(mapping);
      this.updateMedia(mapping);
      this.updateRelationship(mapping, selections);
      if (this.urlSelectionActive) this.updateUrl(selections);
      this.dispatchEvent(
        new CustomEvent("app-managed-variant:changed", {
          bubbles: true,
          detail: {
            baseVariant: this.currentVariant,
            selections,
            mapping: mapping || null,
          },
        }),
      );
    }

    recoverInvalidSelection(selections) {
      const candidates = (this.config.variants || []).filter(
        (mapping) =>
          normalizeVariantId(mapping.baseVariantId) ===
          normalizeVariantId(this.currentVariant?.id),
      );
      if (candidates.length === 0) return null;
      candidates.sort((left, right) => {
        const score = (mapping) =>
          this.config.options.reduce(
            (count, option) =>
              count +
              (String(mapping.optionValueIds?.[option.id]) ===
              String(selections[option.id]?.id)
                ? 0
                : 1),
            0,
          );
        return score(left) - score(right);
      });
      const recovered = candidates[0];
      this.config.options.forEach((option) => {
        const valueId = recovered.optionValueIds?.[option.id];
        const group = this.findOptionGroup(option.id);
        const radio = Array.from(
          group?.querySelectorAll(".app-managed-variant-option__radio") || [],
        ).find((input) => String(input.value) === String(valueId));
        if (radio) radio.checked = true;
      });
      this.urlSelectionActive = true;
      return this.getSelections();
    }

    setOptionInputAvailability(input, available) {
      if (!input) return;
      if (!available && input.dataset.appManagedUnavailable !== "true") {
        input.dataset.appManagedUnavailable = "true";
        input.dataset.appManagedOriginallyDisabled = input.disabled
          ? "true"
          : "false";
        input.disabled = true;
      } else if (
        available &&
        input.dataset.appManagedUnavailable === "true"
      ) {
        input.disabled =
          input.dataset.appManagedOriginallyDisabled === "true";
        delete input.dataset.appManagedUnavailable;
        delete input.dataset.appManagedOriginallyDisabled;
      }
      const label = input.id
        ? this.productScope.querySelector(
            `label[for="${CSS.escape(input.id)}"]`,
          )
        : null;
      const wrapper = input.closest(
        ".block-swatch, .color-swatch, .variant-swatch",
      );
      const unavailableClass = wrapper?.classList.contains("color-swatch")
        ? "color-swatch--disabled"
        : wrapper?.classList.contains("variant-swatch")
          ? "variant-swatch--disabled"
          : "block-swatch--disabled";
      const hideSoldOut = Boolean(
        this.readProductData()?.hide_sold_out_option_values,
      );
      const shouldHide = !available && hideSoldOut;
      if (wrapper) {
        wrapper.classList.toggle(
          "product-option-value--sold-out-hidden",
          shouldHide,
        );
        wrapper.classList.toggle(unavailableClass, !available && !shouldHide);
        wrapper.setAttribute("aria-hidden", shouldHide ? "true" : "false");
      }
      [label, wrapper].filter(Boolean).forEach((element) => {
        if (!available) element.setAttribute("aria-disabled", "true");
        else element.removeAttribute("aria-disabled");
      });
    }

    updateOptionAvailability(selections) {
      this.config.options.forEach((option) => {
        const group = this.findOptionGroup(option.id);
        group
          ?.querySelectorAll(".app-managed-variant-option__radio")
          .forEach((input) => {
            const candidateSelections = {
              ...selections,
              [option.id]: {
                id: input.value,
                label: input.dataset.label || input.value,
                name: group.dataset.optionName || option.name,
              },
            };
            this.setOptionInputAvailability(
              input,
              Boolean(
                this.findMapping(
                  this.currentVariant?.id,
                  candidateSelections,
                ),
              ),
            );
          });
      });

      const productVariants = this.readProductData()?.product?.variants || [];
      const selectedNativeOptions = Array.isArray(this.currentVariant?.options)
        ? [...this.currentVariant.options]
        : [];
      this.productScope
        .querySelectorAll(
          '.product-form__option[data-option-position]:not(.no-js) input[type="radio"]',
        )
        .forEach((input) => {
          const position = Number.parseInt(
            input.dataset.optionPosition ||
              input.closest("[data-option-position]")?.dataset.optionPosition ||
              "",
            10,
          );
          if (!Number.isFinite(position) || position < 1) return;
          const candidateOptions = [...selectedNativeOptions];
          candidateOptions[position - 1] = input.value;
          const candidateVariant = productVariants.find(
            (variant) =>
              Array.isArray(variant.options) &&
              variant.options.length === candidateOptions.length &&
              variant.options.every(
                (value, index) =>
                  String(value) === String(candidateOptions[index]),
              ),
          );
          const available = Boolean(
            candidateVariant &&
              candidateVariant.available !== false &&
              this.findMapping(candidateVariant.id, selections),
          );
          this.setOptionInputAvailability(input, available);
        });
    }

    updatePurchaseAvailability(available) {
      this.productScope
        .querySelectorAll(".product-form__add-button")
        .forEach((button) => {
          if (!available && button.dataset.appManagedUnavailable !== "true") {
            button.dataset.appManagedUnavailable = "true";
            button.dataset.appManagedOriginallyDisabled = button.disabled
              ? "true"
              : "false";
            button.disabled = true;
          } else if (
            available &&
            button.dataset.appManagedUnavailable === "true"
          ) {
            button.disabled =
              button.dataset.appManagedOriginallyDisabled === "true";
            delete button.dataset.appManagedUnavailable;
            delete button.dataset.appManagedOriginallyDisabled;
          }
        });
    }

    updateUrl(selections) {
      const url = new URL(window.location.href);
      const variantId = normalizeVariantId(this.currentVariant?.id);
      if (variantId) url.searchParams.set("variant", variantId);
      const encodedOptions = {};
      this.config.options.forEach((option) => {
        const selection = selections[option.id];
        if (!selection) return;
        encodedOptions[optionPayloadKey(option.name)] = String(selection.id);
        url.searchParams.delete(urlHandle(option.name));
      });
      url.searchParams.set("options", encodeOptionsPayload(encodedOptions));
      window.history.replaceState(window.history.state, "", url.toString());
    }

    updateSku(mapping) {
      const sku = mapping ? mapping.sku || "" : "";
      const skuElement = this.productScope.querySelector(".product-meta__sku");
      const skuNumber = skuElement?.querySelector(".product-meta__sku-number");
      if (skuNumber) skuNumber.textContent = sku;
      if (skuElement) skuElement.style.display = sku ? "" : "none";
      const skuInput = this.productForm?.querySelector(".product-property__sku");
      if (skuInput) skuInput.value = sku;
    }

    updatePrices(mapping) {
      if (!mapping) {
        this.productScope
          .querySelectorAll(
            ".product-unique-price.main-price, .sticky-atc-price, .product-meta__msrp",
          )
          .forEach((element) => {
            element.style.display = "none";
          });
        return;
      }
      const mappedPrice = decimalPriceToCents(mapping?.price);
      const mappedCompareAt = decimalPriceToCents(mapping?.compareAtPrice);
      const price = mappedPrice;
      const compareAt = mappedCompareAt;
      if (!Number.isFinite(price)) return;
      this.productScope
        .querySelectorAll(".product-unique-price.main-price, .sticky-atc-price")
        .forEach((priceList) => {
          const stickyLabel = priceList.classList.contains("sticky-atc-price")
            ? `<span class="price total-price">${this.dataset.priceLabel || "Price"}</span>`
            : "";
          const priceMarkup =
            Number.isFinite(compareAt) && compareAt > price
              ? `<span class="price price--highlight">${formatMoney(price)}</span><span class="price price--compare">${formatMoney(compareAt)}</span>`
              : `<span class="price">${formatMoney(price)}</span>`;
          priceList.innerHTML = stickyLabel + priceMarkup;
          priceList.style.display = "";
        });

      this.productScope
        .querySelectorAll(".product-meta__msrp")
        .forEach((msrp) => {
          const msrpPrice = msrp.querySelector(".product-meta__msrp-price");
          if (Number.isFinite(compareAt) && compareAt > 0) {
            if (msrpPrice) msrpPrice.innerHTML = formatMoney(compareAt);
            msrp.style.display = "";
          } else {
            msrp.style.display = "none";
          }
        });

      this.updateAffirmPrice(price);
    }

    updateAffirmPrice(price) {
      if (!Number.isFinite(price)) return;

      this.affirmAmount = String(Math.round(price));
      if (!this.affirmObserver) {
        this.affirmObserver = new MutationObserver(() => {
          if (this.affirmSyncScheduled) return;
          this.affirmSyncScheduled = true;
          window.requestAnimationFrame(() => {
            this.affirmSyncScheduled = false;
            this.syncAffirmPrice();
          });
        });
        this.affirmObserver.observe(this.productScope, {
          attributes: true,
          attributeFilter: ["data-amount"],
          childList: true,
          subtree: true,
        });
      }

      this.syncAffirmPrice();
    }

    syncAffirmPrice() {
      if (!this.isConnected || !this.affirmAmount) return;

      let amountChanged = false;
      this.productScope
        .querySelectorAll(".affirm-as-low-as")
        .forEach((promo) => {
          if (promo.dataset.amount === this.affirmAmount) return;
          promo.dataset.amount = this.affirmAmount;
          amountChanged = true;
        });
      if (!amountChanged) return;

      const refresh = () => {
        if (this.isConnected && window.affirm?.ui?.refresh) {
          window.affirm.ui.refresh();
        }
      };
      if (window.affirm?.ui?.ready) window.affirm.ui.ready(refresh);
      else refresh();
    }

    updateMedia(mapping) {
      this.restoreManagedMediaOverride();

      const mappedImages = Array.isArray(mapping?.images) && mapping.images.length
        ? mapping.images
        : mapping?.image
          ? [{
              url: mapping.image,
              mediaId: mapping.imageMediaId || "",
              alt: mapping.imageAlt || "",
            }]
          : [];
      if (
        !mapping ||
        (mappedImages.length === 0 &&
          !this.mediaSelectionActive &&
          !this.urlSelectionActive)
      ) {
        this.activeMappedMediaIds = [];
        this.productScope.dispatchEvent(
          new CustomEvent("app-managed-gallery:changed", {
            bubbles: true,
            detail: { mediaIds: [] },
          }),
        );
        return;
      }

      const resolvedImages = mappedImages.map((mappedImage) => {
        let mediaId = normalizeMediaId(mappedImage.mediaId);
        if (!mediaId && mappedImage.url) {
          const mappingImageKey = imageUrlKey(mappedImage.url);
          const matchingImage = Array.from(
            this.productScope.querySelectorAll(
              ".product-gallery__carousel-item .product-gallery__image",
            ),
          ).find((image) =>
            [image.currentSrc, image.src, image.dataset.zoom]
              .some((source) => imageUrlKey(source) === mappingImageKey),
          );
          mediaId = normalizeMediaId(
            matchingImage
              ?.closest(".product-gallery__carousel-item")
              ?.dataset.mediaId,
          );
        }
        return { ...mappedImage, mediaId };
      });
      const mediaIds = resolvedImages.map((image) => image.mediaId).filter(Boolean);
      this.activeMappedMediaIds = mediaIds;
      this.productScope.dispatchEvent(
        new CustomEvent("app-managed-gallery:changed", {
          bubbles: true,
          detail: { mediaIds },
        }),
      );

      let mediaId = mediaIds[0] || "";
      if (!mediaId) {
        mediaId =
          normalizeMediaId(this.currentVariant?.featured_media?.id) ||
          normalizeMediaId(
            this.productScope.querySelector(
              ".product-gallery__carousel-item.is-selected",
            )?.dataset.mediaId,
          ) ||
          normalizeMediaId(
            this.productScope.querySelector(
              ".product-gallery__carousel-item[data-media-id]",
            )?.dataset.mediaId,
          );
      }
      if (!mediaId) return;
      const selector = `[data-media-id="${CSS.escape(mediaId)}"]`;
      const thumbnail = this.productScope.querySelector(
        `.product-gallery__thumbnail${selector}`,
      );
      if (thumbnail) {
        if (!thumbnail.classList.contains("is-nav-selected")) thumbnail.click();
      }

      const primaryImage = resolvedImages[0];
      if (primaryImage?.url) {
        const mappedImageKey = imageUrlKey(primaryImage.url);
        const selectedImage = this.productScope.querySelector(
          ".product-gallery__carousel-item.is-selected .product-gallery__image",
        );
        const selectedImageKey = imageUrlKey(
          selectedImage?.currentSrc || selectedImage?.src,
        );
        if (selectedImage && selectedImageKey !== mappedImageKey) {
          this.overrideSelectedMedia(
            primaryImage.url,
            primaryImage.alt || mapping.linkedProductTitle || "",
          );
          window.requestAnimationFrame(() => {
            if (this.isConnected) {
              this.overrideSelectedMedia(
                primaryImage.url,
                primaryImage.alt || mapping.linkedProductTitle || "",
              );
            }
          });
        }
      }
    }

    rememberMediaAttributes(element) {
      if (!element || element.dataset.appManagedOriginalReady === "true") return;
      element.dataset.appManagedOriginalReady = "true";
      element.dataset.appManagedOriginalAttributes = JSON.stringify(
        Object.fromEntries(
          ["src", "srcset", "href", "data-zoom", "alt"].map((name) => [
            name,
            element.getAttribute(name),
          ]),
        ),
      );
    }

    restoreManagedMediaOverride() {
      this.productScope
        .querySelectorAll('[data-app-managed-original-ready="true"]')
        .forEach((element) => {
          let attributes = {};
          try {
            attributes = JSON.parse(
              element.dataset.appManagedOriginalAttributes || "{}",
            );
          } catch (_) {}
          ["src", "srcset", "href", "data-zoom", "alt"].forEach((name) => {
            if (attributes[name] !== null && attributes[name] !== undefined) {
              element.setAttribute(name, attributes[name]);
            } else {
              element.removeAttribute(name);
            }
          });
          delete element.dataset.appManagedOriginalReady;
          delete element.dataset.appManagedOriginalAttributes;
        });
    }

    overrideSelectedMedia(imageUrl, alt) {
      const selectedItem = this.productScope.querySelector(
        ".product-gallery__carousel-item.is-selected",
      );
      const selectedId = selectedItem?.dataset.mediaId;
      if (!selectedItem || !selectedId) return;
      const selectedThumbnail = this.productScope.querySelector(
        `.product-gallery__thumbnail[data-media-id="${CSS.escape(selectedId)}"]`,
      );
      const images = [
        ...selectedItem.querySelectorAll(".product-gallery__image"),
        ...Array.from(
          selectedThumbnail?.querySelectorAll("img") || [],
        ),
      ];
      images.forEach((image) => {
        this.rememberMediaAttributes(image);
        image.src = imageUrl;
        image.srcset = imageUrl;
        if (image.dataset.zoom !== undefined) image.dataset.zoom = imageUrl;
        if (alt) image.alt = alt;
      });
      selectedItem.querySelectorAll("source").forEach((source) => {
        this.rememberMediaAttributes(source);
        source.srcset = imageUrl;
      });
      const imageLink = selectedItem.querySelector("a[href]");
      if (imageLink) {
        this.rememberMediaAttributes(imageLink);
        imageLink.href = imageUrl;
      }
    }

    updateRelationship(mapping, selections) {
      const bundle = this.productScope.querySelector("relationship-bundle");
      if (!bundle || typeof bundle.setAppManagedMapping !== "function") return;
      const ordered = Array.isArray(this.currentVariant?.options)
        ? [...this.currentVariant.options]
        : [];
      const inserts = this.optionGroups
        .map((group) => ({
          index: Number.parseInt(group.dataset.orderIndex || "", 10),
          label: selections[group.dataset.optionId]?.label,
        }))
        .filter((entry) => entry.label)
        .sort((left, right) => left.index - right.index);
      inserts.forEach((entry) => {
        const index = Math.min(Math.max(entry.index, 0), ordered.length);
        ordered.splice(index, 0, entry.label);
      });
      bundle.setAppManagedMapping(mapping || null, ordered);
    }
  }

  if (!customElements.get("app-managed-variant-options")) {
    customElements.define(
      "app-managed-variant-options",
      AppManagedVariantOptions,
    );
  }
})();
